import React, { useState } from "react";
import { Modal } from "antd";
import { File, FileText, FileVideo } from "lucide-react";
import { LuPlay, LuX } from "react-icons/lu";
import { getFileKindFromUrl, normalizeFileUrls } from "../../utils/filePreview";

const FileResponsePreview = ({ value, fileTypeHint, size = "sm" }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const urls = normalizeFileUrls(value);
  const isSmall = size !== "md";
  const imageClass = isSmall
    ? "relative h-10 w-10 overflow-hidden rounded-lg border-0 bg-gray-100 p-0 cursor-pointer"
    : "relative h-20 w-20 overflow-hidden rounded-lg border-0 bg-gray-100 p-0 cursor-pointer";
  const videoClass = isSmall
    ? "relative h-10 w-10 overflow-hidden rounded-lg border-0 bg-gray-200 p-0 cursor-pointer"
    : "relative h-24 w-48 overflow-hidden rounded-lg border-0 bg-gray-200 p-0 cursor-pointer";

  if (!urls.length) {
    return <div className="text-gray-500">No file uploaded</div>;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {urls.map((url, index) => {
          const kind = getFileKindFromUrl(url, fileTypeHint);

          if (kind === "image") {
            return (
              <button
                key={`${url}-${index}`}
                type="button"
                className={imageClass}
                onClick={() => setPreviewUrl(url)}
              >
                <img
                  src={url}
                  alt={`Uploaded file ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            );
          }

          if (kind === "video") {
            return (
              <button
                key={`${url}-${index}`}
                type="button"
                className={videoClass}
                onClick={() => setPreviewUrl(url)}
              >
                <div className="flex h-full w-full items-center justify-center">
                  <FileVideo className="text-gray-500" size={isSmall ? 16 : 24} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`flex items-center justify-center rounded-full bg-black/50 ${isSmall ? "h-6 w-6" : "h-10 w-10"}`}>
                    <LuPlay className="text-white" size={isSmall ? 12 : 20} />
                  </div>
                </div>
              </button>
            );
          }

          return (
            <a
              key={`${url}-${index}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:underline"
            >
              {kind === "document" ? <FileText size={20} /> : <File size={20} />}
              <span>
                {urls.length > 1
                  ? kind === "document"
                    ? `View Document ${index + 1}`
                    : `View File ${index + 1}`
                  : kind === "document"
                    ? "View Document"
                    : "View File"}
              </span>
            </a>
          );
        })}
      </div>

      <Modal
        open={Boolean(previewUrl)}
        onCancel={() => setPreviewUrl(null)}
        footer={null}
        zIndex={2000}
        closeIcon={
          <div className="bg-secondary flex h-8 w-8 items-center justify-center rounded-full">
            <LuX className="text-red-400" size={20} />
          </div>
        }
      >
        {previewUrl && getFileKindFromUrl(previewUrl, fileTypeHint) === "image" ? (
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
            }}
          />
        ) : previewUrl &&
          getFileKindFromUrl(previewUrl, fileTypeHint) === "video" ? (
          <video
            src={previewUrl}
            controls
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
            }}
          />
        ) : (
          <div className="flex items-center justify-center p-8">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Open file in new tab
            </a>
          </div>
        )}
      </Modal>
    </>
  );
};

export default FileResponsePreview;
